import { useState } from "react";
import { useNavigate } from "react-router-dom";

const PLANS = [
  {
    id: "basic",
    name: "Basic",
    price: "$9.99",
    period: "/month",
    tagline: "Great for getting started",
    highlighted: false,
    features: [
      { text: "Up to 10 job applications/month", available: true },
      { text: "Basic capability matching", available: true },
      { text: "Job board access", available: true },
      { text: "Availability toggle", available: true },
      { text: "AI resume generation", available: false },
      { text: "Auto-fill applications", available: false },
      { text: "Priority job matching", available: false },
      { text: "Profile optimization", available: false },
    ],
    cta: "Start Basic",
    ctaStyle: "border border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-600",
  },
  {
    id: "pro",
    name: "Pro",
    price: "$29.99",
    period: "/month",
    tagline: "For serious earners",
    highlighted: true,
    badge: "Most Popular",
    features: [
      { text: "Unlimited job applications", available: true },
      { text: "AI-powered capability matching", available: true },
      { text: "Full marketplace access", available: true },
      { text: "Availability toggle + live alerts", available: true },
      { text: "AI resume + cover letter generation", available: true },
      { text: "Semi-automated auto-fill apply", available: true },
      { text: "Priority job matching", available: true },
      { text: "Profile optimization included", available: true },
    ],
    cta: "Start Pro Free",
    ctaStyle: "bg-blue-600 text-white hover:bg-blue-700",
  },
  {
    id: "enterprise",
    name: "Employer",
    price: "Custom",
    period: "",
    tagline: "For teams & businesses",
    highlighted: false,
    features: [
      { text: "Unlimited job postings", available: true },
      { text: "Real-time worker matching", available: true },
      { text: "Urgency job broadcasting", available: true },
      { text: "Candidate management dashboard", available: true },
      { text: "Team accounts", available: true },
      { text: "API access", available: true },
      { text: "10–20% commission per job", available: true },
      { text: "Dedicated account manager", available: true },
    ],
    cta: "Contact Sales",
    ctaStyle: "border border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-600",
  },
];

type FormState = { name: string; email: string; plan: string };

export default function PricingSection() {
  const [billing] = useState<"monthly" | "annual">("monthly");
  const [formState, setFormState] = useState<FormState>({ name: "", email: "", plan: "Pro" });
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const data = new URLSearchParams();
    data.append("name", formState.name);
    data.append("email", formState.email);
    data.append("plan", formState.plan);
    try {
      const res = await fetch("https://readdy.ai/api/form/d75cv9ivu4krcbn3bkhg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok || res.status === 200) {
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="pricing" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full mb-4">
            Simple Pricing
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Start Free. Grow Fast.
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            14-day free trial on all plans. No credit card required. Cancel anytime.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border p-7 flex flex-col gap-5 transition-all duration-200 ${
                plan.highlighted
                  ? "border-blue-500 bg-blue-600 shadow-xl shadow-blue-200"
                  : "border-gray-200 bg-white hover:shadow-md"
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-orange-500 text-white text-xs font-extrabold px-4 py-1.5 rounded-full whitespace-nowrap">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div>
                <div className={`text-xs font-bold uppercase tracking-widest mb-1 ${plan.highlighted ? "text-blue-200" : "text-gray-400"}`}>
                  {plan.name}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-black ${plan.highlighted ? "text-white" : "text-gray-900"}`}>
                    {plan.price}
                  </span>
                  <span className={`text-sm font-medium ${plan.highlighted ? "text-blue-200" : "text-gray-400"}`}>
                    {plan.period}
                  </span>
                </div>
                <p className={`text-sm mt-1 ${plan.highlighted ? "text-blue-200" : "text-gray-500"}`}>{plan.tagline}</p>
              </div>

              {/* Features */}
              <ul className="flex flex-col gap-2.5">
                {plan.features.map((f) => (
                  <li key={f.text} className="flex items-start gap-2.5">
                    <span
                      className={`mt-0.5 w-4 h-4 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${
                        f.available
                          ? plan.highlighted
                            ? "bg-white/20 text-white"
                            : "bg-green-100 text-green-700"
                          : plan.highlighted
                          ? "bg-white/10 text-blue-300"
                          : "bg-gray-100 text-gray-300"
                      }`}
                    >
                      {f.available ? <i className="ri-check-line" /> : <i className="ri-close-line" />}
                    </span>
                    <span
                      className={`text-sm leading-relaxed ${
                        f.available
                          ? plan.highlighted
                            ? "text-white"
                            : "text-gray-700"
                          : plan.highlighted
                          ? "text-blue-300/60"
                          : "text-gray-300"
                      }`}
                    >
                      {f.text}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  if (plan.id === "enterprise") return;
                  navigate(`/checkout?plan=${plan.id}&billing=monthly`);
                }}
                className={`mt-auto py-3 font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap ${plan.ctaStyle}`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Upsells Banner */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-14 flex flex-col md:flex-row items-center gap-6">
          <div className="flex-1">
            <h3 className="font-extrabold text-gray-900 text-lg mb-1">Optional Add-ons</h3>
            <p className="text-sm text-gray-500">Boost your profile and earning potential with premium upgrades</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Profile Optimization", price: "$19 one-time", icon: "ri-user-star-line" },
              { label: "Professional Resume Rewrite", price: "$49 one-time", icon: "ri-file-text-line" },
              { label: "Priority Listing Boost", price: "$9.99/week", icon: "ri-rocket-2-line" },
            ].map((addon) => (
              <div key={addon.label} className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-2.5">
                <i className={`${addon.icon} text-blue-600`} />
                <div>
                  <div className="text-xs font-bold text-gray-800">{addon.label}</div>
                  <div className="text-xs text-gray-500">{addon.price}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Free Trial Form */}
        <div id="trial-form" className="max-w-lg mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-extrabold text-gray-900 mb-2">Start Your Free 14-Day Trial</h3>
            <p className="text-gray-500 text-sm">No credit card required. Access everything for free, cancel anytime.</p>
          </div>

          {submitted ? (
            <div className="text-center py-12 bg-green-50 rounded-2xl border border-green-200">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-check-double-line text-green-600 text-2xl" />
              </div>
              <h4 className="font-extrabold text-gray-900 text-xl mb-2">You're on the list!</h4>
              <p className="text-gray-500 text-sm max-w-xs mx-auto">
                Check your inbox — your free trial access is on the way. Start earning within minutes.
              </p>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl border border-gray-200 p-7 flex flex-col gap-4"
            >
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Full Name</label>
                <input
                  name="name"
                  type="text"
                  required
                  value={formState.name}
                  onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address</label>
                <input
                  name="email"
                  type="email"
                  required
                  value={formState.email}
                  onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Plan</label>
                <select
                  name="plan"
                  value={formState.plan}
                  onChange={(e) => setFormState({ ...formState, plan: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                >
                  <option value="Basic">Basic — $9.99/month</option>
                  <option value="Pro">Pro — $29.99/month</option>
                </select>
              </div>
              {error && <p className="text-xs text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-blue-600 text-white font-bold rounded-xl text-sm hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" /> Processing...
                  </span>
                ) : (
                  <>
                    <i className="ri-flashlight-line mr-2" />
                    Start Free Trial — No Card Needed
                  </>
                )}
              </button>
              <p className="text-xs text-center text-gray-400">
                14 days free · Cancel anytime · Join 12,000+ workers already earning
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
